/* eslint-disable @typescript-eslint/no-explicit-any */

'use client'

import { Slot } from '@radix-ui/react-slot'
import { isEmpty } from 'lodash-es'
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  createContext,
  FocusEventHandler,
  FormEventHandler,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
  useContext,
  useId,
} from 'react'
import {
  FieldPath,
  FieldValues,
  FormProvider,
  RefCallBack,
  useController,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form'

import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui'

import { Label, type Props as LabelProps } from '../label'
import { Message } from '../message'

const Form = <TFieldValues extends FieldValues>({
  children,
  ...props
}: ComponentProps<'form'> & { form: UseFormReturn<TFieldValues> }) => {
  return (
    <FormProvider {...props.form}>
      <form
        {...props}
        onSubmit={event => {
          event.preventDefault()
        }}
      >
        {children}
      </form>
    </FormProvider>
  )
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

export const FormItem = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => {
  const id = useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={className} {...props} />
    </FormItemContext.Provider>
  )
}

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
  disabled: boolean
  readOnly: boolean
}

const FormFieldContext = createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

type FormFieldProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    name: string
    readOnly?: boolean
    disabled?: boolean
  }
>

const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    { name, disabled = false, readOnly = false, children, className, ...props },
    ref,
  ) => {
    return (
      <FormFieldContext.Provider
        value={{ name, disabled, readOnly } as FormFieldContextValue}
      >
        <FormItem ref={ref} className={cn('space-y-2', className)} {...props}>
          {children}
        </FormItem>
      </FormFieldContext.Provider>
    )
  },
)
FormField.displayName = 'FormField'

export const useFormField = () => {
  const fieldContext = useContext(FormFieldContext)
  const itemContext = useContext(FormItemContext)

  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (isEmpty(fieldContext)) {
    throw new Error('useFormField should be used within <FormField>')
  }

  if (isEmpty(itemContext)) {
    throw new Error('useFormField should be used within <FormItem>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    readOnly: fieldContext.readOnly,
    disabled: fieldContext.disabled,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

const FormLabel = ({ className, ...props }: LabelProps) => {
  const { formItemId } = useFormField()

  return <Label className={cn(className)} htmlFor={formItemId} {...props} />
}

export type FieldOnChange = (...event: any[]) => void

type FormControlProps = ComponentPropsWithoutRef<typeof Slot> & {
  render?: (
    props: Omit<ComponentPropsWithoutRef<typeof Slot>, 'onChange' | 'color'> & {
      value: any
      onChange: FieldOnChange
      ref: RefCallBack
    },
  ) => ReactNode
  resetErrorOnBlur?: boolean
}

const FormControl = ({
  onChange: onChangeProp,
  onBlur: onBlurProp,
  render,
  resetErrorOnBlur = true,
  ...restProps
}: FormControlProps) => {
  const { control, clearErrors } = useFormContext()

  const {
    name,
    error,
    readOnly,
    disabled,
    formItemId,
    formDescriptionId,
    formMessageId,
  } = useFormField()

  const {
    field: { onChange, onBlur, ...field },
  } = useController({
    name,
    control,
  })

  const handleChange: FormEventHandler<HTMLElement> = event => {
    if (error) {
      clearErrors(name)
    }

    onChange(event)
    onChangeProp?.(event)
  }

  const handleBlur: FocusEventHandler<HTMLElement> = event => {
    if (error && resetErrorOnBlur) {
      onBlur()
    }

    onBlurProp?.(event)
  }

  const props = {
    id: formItemId,
    'aria-describedby': !error
      ? formDescriptionId
      : `${formDescriptionId} ${formMessageId}`,
    'aria-invalid': !!error,
    'aria-disabled': disabled,
    'aria-readonly': readOnly,
    disabled,
    onChange: handleChange,
    onBlur: handleBlur,
    ...field,
    ...restProps,
  }

  if (render) {
    return render(props)
  }

  return (
    <Slot
      id={formItemId}
      aria-describedby={
        !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      {...field}
      {...restProps}
    />
  )
}

const FormDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <Message
      ref={ref}
      id={formDescriptionId}
      className={className}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error.message) : children

  if (!body) {
    return null
  }

  return (
    <Message
      ref={ref}
      id={formMessageId}
      type={error ? 'error' : 'default'}
      className={cn('flex items-center', className)}
      {...props}
    >
      <Icon name="alert-filled" size={20} />
      {body}
    </Message>
  )
})
FormMessage.displayName = 'FormMessage'

export { Form, FormField, FormLabel, FormControl, FormMessage, FormDescription }
